import React, { useMemo, forwardRef, useImperativeHandle, useRef } from 'react'
import styled from 'styled-components'
import ReactECharts from 'echarts-for-react'
import type { TFunction } from 'i18next'
import {
  getIDCStatus,
  IDC_BASE_THRESHOLD,
  IDC_OPTIMIZATION_THRESHOLD,
  getSignificantThreshold,
} from '../utils/idcCalculations'

export interface IDCGaugeChartRef {
  exportChart: () => string | null
}

interface IDCGaugeChartProps {
  idc: number // MJ/m²·year
  currentYear?: number
  t: TFunction
}

const ChartContainer = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: 20px;
  box-shadow: ${({ theme }) => theme.shadows.sm};
`

const ChartTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 12px 0;
  text-align: center;
`

const ThresholdLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.border.default};
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
`

const LegendColor = styled.div<{ $color: string }>`
  width: 16px;
  height: 10px;
  background: ${({ $color }) => $color};
  border-radius: 2px;
`

const LegendLabel = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const IDCGaugeChart = forwardRef<IDCGaugeChartRef, IDCGaugeChartProps>(
  ({ idc, currentYear = new Date().getFullYear(), t }, ref) => {
    const echartsRef = useRef<any>(null)

    useImperativeHandle(ref, () => ({
      exportChart: () => {
        if (echartsRef.current) {
          const instance = echartsRef.current.getEchartsInstance()
          if (instance) {
            return instance.getDataURL({
              type: 'png',
              pixelRatio: 2,
              backgroundColor: '#fff',
            })
          }
        }
        return null
      },
    }))

    const status = getIDCStatus(idc, currentYear)
    const significantThreshold = getSignificantThreshold(currentYear)

    const option = useMemo(() => {
      // Maximum value for gauge (add some padding above the highest threshold)
      const maxValue = Math.max(1000, significantThreshold + 200, idc + 100)

      return {
        series: [
          {
            type: 'gauge',
            min: 0,
            max: maxValue,
            splitNumber: 5,
            radius: '75%',
            center: ['50%', '55%'],
            startAngle: 200,
            endAngle: -20,

            // Main axis line (the colored arc)
            axisLine: {
              lineStyle: {
                width: 30,
                color: [
                  [IDC_BASE_THRESHOLD / maxValue, '#10b981'], // Green: 0-450 (compliant)
                  [IDC_OPTIMIZATION_THRESHOLD / maxValue, '#eab308'], // Yellow: 450-550 (optimization)
                  [significantThreshold / maxValue, '#f97316'], // Orange: 550-significant (warning)
                  [1, '#ef4444'], // Red: above significant (critical)
                ],
              },
            },

            // Pointer (needle)
            pointer: {
              width: 6,
              length: '75%',
              itemStyle: {
                color: status.color,
                shadowColor: 'rgba(0, 0, 0, 0.3)',
                shadowBlur: 5,
              },
            },

            // Tick marks
            axisTick: {
              distance: -30,
              length: 8,
              lineStyle: {
                color: '#fff',
                width: 2,
              },
            },

            // Split lines (major divisions)
            splitLine: {
              distance: -30,
              length: 15,
              lineStyle: {
                color: '#fff',
                width: 3,
              },
            },

            // Labels on the axis
            axisLabel: {
              distance: 15,
              color: '#64748b',
              fontSize: 12,
              formatter: (value: number) => {
                return Math.round(value).toString()
              },
            },

            // Center detail (shows the IDC value)
            detail: {
              valueAnimation: true,
              formatter: '{value}',
              fontSize: 32,
              fontWeight: 'bold',
              color: status.color,
              offsetCenter: [0, '75%'],
            },

            // Title below the value
            title: {
              offsetCenter: [0, '95%'],
              fontSize: 14,
              color: '#64748b',
            },

            // The actual data
            data: [
              {
                value: Math.round(idc),
                name: t('idcGaugeChart.unit'),
              },
            ],

            // Anchor (center point of the gauge)
            anchor: {
              show: true,
              showAbove: true,
              size: 20,
              itemStyle: {
                color: status.color,
                borderWidth: 4,
                borderColor: '#fff',
                shadowColor: 'rgba(0, 0, 0, 0.2)',
                shadowBlur: 5,
              },
            },
          },
        ],

        // Animation
        animation: true,
        animationDuration: 1000,
        animationEasing: 'cubicOut',
      }
    }, [idc, status.color, significantThreshold, t])

    return (
      <ChartContainer>
        <ChartTitle>{t('idcGaugeChart.title')}</ChartTitle>

        <ReactECharts
          ref={echartsRef}
          option={option}
          style={{ height: '240px', width: '100%' }}
          opts={{ renderer: 'svg' }}
        />

        <ThresholdLegend>
          <LegendItem>
            <LegendColor $color="#10b981" />
            <LegendLabel>
              {t('idcGaugeChart.legend.compliant', {
                threshold: IDC_BASE_THRESHOLD,
              })}
            </LegendLabel>
          </LegendItem>

          <LegendItem>
            <LegendColor $color="#eab308" />
            <LegendLabel>
              {t('idcGaugeChart.legend.optimizationRequired', {
                min: IDC_BASE_THRESHOLD,
                max: IDC_OPTIMIZATION_THRESHOLD,
              })}
            </LegendLabel>
          </LegendItem>

          <LegendItem>
            <LegendColor $color="#f97316" />
            <LegendLabel>
              {t('idcGaugeChart.legend.warning', {
                min: IDC_OPTIMIZATION_THRESHOLD,
                max: significantThreshold,
              })}
            </LegendLabel>
          </LegendItem>

          <LegendItem>
            <LegendColor $color="#ef4444" />
            <LegendLabel>
              {t('idcGaugeChart.legend.critical', {
                threshold: significantThreshold,
              })}
            </LegendLabel>
          </LegendItem>
        </ThresholdLegend>
      </ChartContainer>
    )
  }
)

IDCGaugeChart.displayName = 'IDCGaugeChart'
